import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import Select from "react-select";

import {
    fetchAllBankAccounts,
    fetchAccountBankAccounts,
    patchFinanceOpsJob,
    postFinanceOpsApplyAdjustment,
    submitHrpPayable,
    submitHrpPremiumPayment,
    submitHrpReceivable,
} from "../../utils/apiUtils";
import { customStyles } from "../../utils/styleUtils";
import "./ApplyPaymentPanel.scss";

const maskAccountNumber = (n) => {
    const s = String(n || "").replace(/\s+/g, "");
    if (!s) return null;
    if (s.length <= 4) return s;
    return `••••${s.slice(-4)}`;
};

const bankOptionFromRow = (row) => {
    const name = String(row?.accountName || row?.bankAccountName || "").trim();
    if (!name) return null;
    const bankName = row?.bankName || null;
    const acct = maskAccountNumber(row?.accountNumber);
    const bits = [name, bankName, acct].filter(Boolean);
    return {
        value: name,
        label: bits.join(" · "),
        bankName,
        accountNumber: row?.accountNumber || null,
        bankAccountKey: row?.bankAccountKey ?? row?.bnkAccKey ?? null,
    };
};

const FUNDING_CATEGORY_OPTIONS = [
    { value: "6", label: "Manual Adjustment" },
    { value: "FRAC01", label: "IDR Reimbursement" },
    { value: "FRAC02", label: "RX Invoice" },
    { value: "FRAC03", label: "Cigna" },
    { value: "FRAC04", label: "NPF" },
    { value: "FRAC05", label: "Cigna Claim Edit" },
    { value: "FRAC06", label: "Bank Fees" },
    { value: "FRAC07", label: "Multiplan" },
    { value: "1", label: "Transaction Fee" },
    { value: "2", label: "Pharmacy" },
    { value: "3", label: "Care Management" },
    { value: "4", label: "Other" },
    { value: "5", label: "Receivable Credit" },
];

const BILLING_REASON_OPTIONS = [
    { value: "BAR02", label: "Claim Processing Fee" },
    { value: "BAR01", label: "Additional ID Cards" },
    { value: "BAR09", label: "Bank Service Fee" },
    { value: "BAR11", label: "Broker Fees" },
    { value: "BAR07", label: "Implementation Credit" },
    { value: "BAR05", label: "PBM Administrative Fee" },
    { value: "1", label: "Discount" },
    { value: "2", label: "Write-off" },
    { value: "3", label: "Settlement" },
    { value: "BAR03", label: "COBRA Qualifying Event" },
    { value: "BAR04", label: "COBRA Administrative Fee" },
    { value: "BAR06", label: "HRA Administrative Fee" },
    { value: "BAR08", label: "Integration Fee" },
    { value: "BAR10", label: "Stoploss Collateral" },
    { value: "BAR12", label: "Self Bill" },
];

const DIRECTION_OPTIONS = [
    { value: "debit", label: "Debit (increase due)" },
    { value: "credit", label: "Credit (decrease due)" },
];

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const formatHrpErrorMessage = (raw) => {
    if (raw == null) return "Unknown HRP error";
    if (typeof raw === "object") {
        raw =
            raw.message ||
            raw.errorMessage ||
            raw.error?.message ||
            raw.error?.errorMessage ||
            JSON.stringify(raw);
    }
    let text = String(raw);
    try {
        const parsed = JSON.parse(text);
        if (parsed?.message) text = parsed.message;
        else if (parsed?.errorMessage) text = parsed.errorMessage;
        else if (parsed?.error != null) {
            text =
                typeof parsed.error === "string"
                    ? parsed.error
                    : parsed.error?.message ||
                      parsed.error?.errorMessage ||
                      JSON.stringify(parsed.error);
        }
    } catch {
        // not JSON
    }

    const faultMatch = text.match(/faultstring>([^<]+)/i);
    if (faultMatch?.[1]) text = faultMatch[1];

    const attrMatch = text.match(
        /([A-Za-z][\w.]*):\s*attribute:\s*'([^']+)'\s*:\s*([^\n]+)/,
    );
    if (attrMatch) {
        return `HRP rejected ${attrMatch[1]}.${attrMatch[2]}: ${attrMatch[3].trim()}`;
    }

    const saxLines = [...text.matchAll(/SAXParseException:\s*([^\n\]]+)/g)].map((m) =>
        m[1].replace(/\s+/g, " ").trim(),
    );
    if (saxLines.length) {
        return `HRP rejected the adjustment payload: ${saxLines[0]}`;
    }
    if (/Validation failed/i.test(text)) {
        const oneOf = text.match(/One of '\{[^}]+\}' is expected[^.]*/);
        if (oneOf) return `HRP schema validation failed. ${oneOf[0]}.`;
        return "HRP schema validation failed for this adjustment payload.";
    }

    text = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (text.length > 420) text = `${text.slice(0, 420)}…`;
    return text || "HRP adjustment submit failed";
};

const asArray = (value) => {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
};

const extractHrpOutcome = (hrpResult) => {
    const responses = asArray(
        hrpResult?.paymentResponse || hrpResult?.payableResponse || hrpResult?.receivableResponse,
    );
    const lineErrors = responses.flatMap((r) => asArray(r?.errors?.error || r?.errors));
    const topErrors = asArray(hrpResult?.errors?.error || hrpResult?.errors);
    const allErrors = [...lineErrors, ...topErrors];
    const lineStatus = String(
        hrpResult?.status || responses[0]?.status || "",
    ).toUpperCase();
    const failed =
        lineStatus.includes("FAIL") ||
        lineStatus.includes("ERROR") ||
        allErrors.length > 0;
    const firstErr = allErrors[0];
    const errMsg =
        (typeof firstErr === "string"
            ? firstErr
            : [firstErr?.message || firstErr?.errorMessage, firstErr?.traceInfo]
                  .filter(Boolean)
                  .join("\n")) ||
        `HRP returned status ${lineStatus || "FAILURE"}`;
    return { responses, failed, errMsg, lineStatus };
};

const submitByEndpoint = async (endpoint, body) => {
    if (endpoint?.includes("/payable/")) return submitHrpPayable(body);
    if (endpoint?.includes("/receivable/")) return submitHrpReceivable(body);
    if (endpoint?.includes("/premiumPayment/")) return submitHrpPremiumPayment(body);
    throw new Error(`Unsupported adjustment endpoint: ${endpoint || "(missing)"}`);
};

/**
 * Apply funding-request or premium-bill adjustment (JSON → connector-api).
 *
 * @param {'funding'|'premium'} kind
 * @param {object} context
 */
const ApplyAdjustmentPanel = ({ kind, context, onClose, onApplied, variant = "inline" }) => {
    const isDrawer = variant === "drawer";
    const queryClient = useQueryClient();
    const [amount, setAmount] = useState("");
    const [direction, setDirection] = useState(DIRECTION_OPTIONS[0]);
    const [category, setCategory] = useState(FUNDING_CATEGORY_OPTIONS[0]);
    const [reason, setReason] = useState(BILLING_REASON_OPTIONS[0]);
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [bankAccountName, setBankAccountName] = useState(
        () => context?.bankAccountName || context?.accountHccId || "",
    );
    const [bankOption, setBankOption] = useState(null);
    const [supplierHccId, setSupplierHccId] = useState(context?.supplierHccId || "");
    const [comments, setComments] = useState("");
    const [resultMsg, setResultMsg] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const accountHccId = context?.accountHccId || null;
    const accountKey = context?.accountKey ?? null;

    const banksQuery = useQuery({
        queryKey: ["account-banks-for-adjustment", accountHccId, accountKey],
        queryFn: async () => {
            const [byName, byAccount] = await Promise.all([
                accountHccId
                    ? fetchAllBankAccounts({
                          accountName: accountHccId,
                          limit: 20,
                      }).catch(() => ({ rows: [] }))
                    : Promise.resolve({ rows: [] }),
                accountKey != null || accountHccId
                    ? fetchAccountBankAccounts({
                          ...(accountKey != null ? { acctIdKey: accountKey } : {}),
                          ...(accountHccId ? { accountHccId } : {}),
                          limit: 20,
                      }).catch(() => ({ rows: [] }))
                    : Promise.resolve({ rows: [] }),
            ]);
            const rows = [
                ...(Array.isArray(byName?.rows) ? byName.rows : []),
                ...(Array.isArray(byAccount?.rows) ? byAccount.rows : []),
            ];
            const seen = new Set();
            const options = [];
            for (const row of rows) {
                const opt = bankOptionFromRow(row);
                if (!opt || seen.has(opt.value)) continue;
                seen.add(opt.value);
                options.push(opt);
            }
            // HRP Payable bankAccountName is typically the account HCC id (BANK_ACCOUNT.ACCOUNT_NAME).
            if (accountHccId && !seen.has(accountHccId)) {
                options.unshift({
                    value: accountHccId,
                    label: `${accountHccId} · (account HCC)`,
                    bankName: null,
                    accountNumber: null,
                    bankAccountKey: null,
                });
            }
            return options;
        },
        enabled: kind === "funding" && !!(accountHccId || accountKey != null),
        staleTime: 60_000,
        retry: false,
    });

    const bankOptions = banksQuery.data || [];

    useEffect(() => {
        if (kind !== "funding" || !bankOptions.length) return;
        const preferred =
            bankOptions.find((o) => o.value === bankAccountName) ||
            bankOptions.find((o) => o.value === accountHccId) ||
            bankOptions[0];
        if (!preferred) return;
        setBankOption(preferred);
        if (preferred.value !== bankAccountName) {
            setBankAccountName(preferred.value);
        }
        // Only auto-select when banks first load / account changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, accountHccId, bankOptions]);

    const title = kind === "premium" ? "Apply billing adjustment" : "Apply funding adjustment";
    const hint =
        kind === "premium"
            ? "Posts a billing adjustment against this premium bill via connector-api (submitPremiumPayment adjustment detail)."
            : "Posts a funding-request adjustment via connector-api (submitPayable / submitReceivable). JSON only — no client SOAP.";

    const fields = useMemo(() => {
        const base = {
            adjustmentAmount: amount,
            asOfDate: toIsoDate(asOfDate),
            comments: comments || undefined,
            accountKey: context?.accountKey,
            accountHccId: context?.accountHccId,
            accountName: context?.accountName,
            bankAccountName: bankAccountName || undefined,
        };
        if (kind === "funding") {
            return {
                ...base,
                direction: direction?.value || "debit",
                categoryCode: category?.value || "6",
                fundingRequestId: context?.fundingRequestId ?? context?.key,
                paymentCycleId: context?.paymentCycleId || context?.cycleKey,
                supplierHccId: supplierHccId || undefined,
            };
        }
        return {
            ...base,
            billId: context?.billId ?? context?.key,
            reasonCode: reason?.value || "BAR02",
            benefitPlanId: context?.benefitPlanId || context?.planHccId,
            planHccId: context?.planHccId,
            billingCategoryCode: context?.billingCategoryCode,
            coverageStartDate: context?.coverageStartDate || context?.billedPeriodStartDate,
            coverageEndDate: context?.coverageEndDate || context?.billedPeriodEndDate,
            billedPeriodStartDate: context?.billedPeriodStartDate,
            billedPeriodEndDate: context?.billedPeriodEndDate,
        };
    }, [
        kind,
        amount,
        asOfDate,
        comments,
        direction,
        category,
        reason,
        bankAccountName,
        supplierHccId,
        context,
    ]);

    const mutation = useMutation({
        mutationFn: async () => {
            setErrorMsg(null);
            setResultMsg(null);
            const prepared = await postFinanceOpsApplyAdjustment({ kind, fields });
            const jobId = prepared?.job?.id;
            const endpoint = prepared?.hrp?.endpoint;
            const body = prepared?.hrp?.body;
            if (!jobId || !endpoint || !body) {
                throw new Error("Adjustment prepare response incomplete");
            }

            try {
                const hrpResult = await submitByEndpoint(endpoint, body);
                const { responses, failed, errMsg } = extractHrpOutcome(hrpResult);

                if (failed) {
                    const message = formatHrpErrorMessage(errMsg);
                    await patchFinanceOpsJob(jobId, {
                        status: "failed",
                        error: message,
                        meta: { ...(prepared.job.meta || {}), hrpResult },
                    });
                    throw new Error(message);
                }

                const corr =
                    hrpResult?.payableIdentifier ||
                    hrpResult?.receivableIdentifier ||
                    hrpResult?.transactionInformation?.transactionId ||
                    responses[0]?.premiumPaymentBatchId ||
                    responses[0]?.payableIdentifier ||
                    responses[0]?.receivableIdentifier ||
                    null;

                await patchFinanceOpsJob(jobId, {
                    status: "succeeded",
                    hrpCorrelationId: corr != null ? String(corr) : null,
                    meta: { ...(prepared.job.meta || {}), hrpResult },
                });
                return { prepared, hrpResult };
            } catch (err) {
                const errMsg = formatHrpErrorMessage(err?.message || err);
                await patchFinanceOpsJob(jobId, {
                    status: "failed",
                    error: errMsg,
                }).catch(() => {});
                throw new Error(errMsg);
            }
        },
        onSuccess: async (result) => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            setErrorMsg(null);
            const amt = result?.prepared?.summary?.adjustmentAmount;
            const baseMsg = `Adjustment applied${amt != null ? ` · ${money(amt)}` : ""}.`;
            setResultMsg(`${baseMsg} Updating totals…`);
            try {
                const syncStatus = await onApplied?.(result);
                if (syncStatus === "optimistic") {
                    setResultMsg(
                        `${baseMsg} Totals updated here — HRP warehouse may take a few minutes to catch up.`,
                    );
                } else {
                    setResultMsg(baseMsg);
                }
            } catch {
                setResultMsg(baseMsg);
            }
        },
        onError: (err) => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            setResultMsg(null);
            setErrorMsg(formatHrpErrorMessage(err?.message || err));
        },
    });

    const amountOk = amount && Number(amount) > 0;

    return (
        <section
            className={`apply-payment${isDrawer ? " apply-payment--drawer" : ""}`}
            aria-label={title}
        >
            {isDrawer ? (
                <p className="apply-payment__lead">{hint}</p>
            ) : (
                <div className="apply-payment__head">
                    <div>
                        <h2>{title}</h2>
                        <p>{hint}</p>
                    </div>
                    {onClose ? (
                        <button type="button" className="apply-payment__link" onClick={onClose}>
                            Close
                        </button>
                    ) : null}
                </div>
            )}

            <div className="apply-payment__context">
                <span>
                    Account:{" "}
                    <strong>
                        {context?.accountName || context?.accountHccId || "—"}
                        {context?.accountHccId ? ` (${context.accountHccId})` : ""}
                    </strong>
                </span>
                <span>
                    {kind === "premium" ? "Bill" : "Funding request"}:{" "}
                    <strong>#{context?.key ?? context?.billId ?? context?.fundingRequestId}</strong>
                </span>
                {kind === "funding" && (context?.paymentCycleId || context?.cycleKey) ? (
                    <span>
                        Cycle: <strong>{context.paymentCycleId || context.cycleKey}</strong>
                    </span>
                ) : null}
            </div>

            <div className="apply-payment__form">
                <div className="apply-payment__field">
                    <label htmlFor="apply-adj-amount">Amount</label>
                    <input
                        id="apply-adj-amount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                    />
                </div>

                {kind === "funding" ? (
                    <>
                        <div className="apply-payment__field">
                            <label>Direction</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={DIRECTION_OPTIONS}
                                value={direction}
                                onChange={(opt) => setDirection(opt || DIRECTION_OPTIONS[0])}
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 300 }),
                                }}
                            />
                        </div>
                        <div className="apply-payment__field">
                            <label>Category</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={FUNDING_CATEGORY_OPTIONS}
                                value={category}
                                onChange={(opt) => setCategory(opt || FUNDING_CATEGORY_OPTIONS[0])}
                                isSearchable
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 300 }),
                                }}
                            />
                        </div>
                        <div className="apply-payment__field apply-payment__field--wide">
                            <label>Bank account</label>
                            {bankOptions.length ? (
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={bankOptions}
                                    value={
                                        bankOption ||
                                        bankOptions.find((o) => o.value === bankAccountName) ||
                                        null
                                    }
                                    onChange={(opt) => {
                                        setBankOption(opt);
                                        setBankAccountName(opt?.value || "");
                                    }}
                                    isLoading={banksQuery.isFetching}
                                    isSearchable
                                    placeholder="Select HRP bank account"
                                    menuPortalTarget={
                                        typeof document !== "undefined" ? document.body : null
                                    }
                                    styles={{
                                        ...customStyles,
                                        menuPortal: (base) => ({ ...base, zIndex: 300 }),
                                    }}
                                />
                            ) : (
                                <input
                                    id="apply-adj-bank"
                                    type="text"
                                    value={bankAccountName}
                                    onChange={(e) => {
                                        setBankOption(null);
                                        setBankAccountName(e.target.value);
                                    }}
                                    placeholder={
                                        banksQuery.isFetching
                                            ? "Loading bank accounts…"
                                            : "Required — HRP bank account name"
                                    }
                                />
                            )}
                            {bankOption?.bankName || bankOption?.accountNumber ? (
                                <span className="apply-payment__hint">
                                    {[bankOption.bankName, maskAccountNumber(bankOption.accountNumber)]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </span>
                            ) : banksQuery.isError ? (
                                <span className="apply-payment__hint apply-payment__hint--warn">
                                    Could not load banks — enter HRP bank account name manually.
                                </span>
                            ) : null}
                        </div>
                        <div className="apply-payment__field">
                            <label htmlFor="apply-adj-supplier">Supplier HCC (optional)</label>
                            <input
                                id="apply-adj-supplier"
                                type="text"
                                value={supplierHccId}
                                onChange={(e) => setSupplierHccId(e.target.value)}
                                placeholder="Uses Supplier payee when set"
                            />
                        </div>
                    </>
                ) : (
                    <div className="apply-payment__field">
                        <label>Billing reason</label>
                        <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={BILLING_REASON_OPTIONS}
                            value={reason}
                            onChange={(opt) => setReason(opt || BILLING_REASON_OPTIONS[0])}
                            isSearchable
                            menuPortalTarget={
                                typeof document !== "undefined" ? document.body : null
                            }
                            styles={{
                                ...customStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 300 }),
                            }}
                        />
                    </div>
                )}

                <div className="apply-payment__field">
                    <label>As-of / release date</label>
                    <DatePicker
                        selected={asOfDate}
                        onChange={(d) => setAsOfDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                        popperClassName="date-picker-popper"
                        popperProps={{ strategy: "fixed" }}
                    />
                </div>
                <div className="apply-payment__field apply-payment__field--wide">
                    <label htmlFor="apply-adj-comments">Comments</label>
                    <input
                        id="apply-adj-comments"
                        type="text"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Optional"
                    />
                </div>
            </div>

            <div className="apply-payment__actions">
                <button
                    type="button"
                    className="btn apply-payment__submit apply-payment__submit--adjust"
                    disabled={
                        mutation.isPending ||
                        !amountOk ||
                        (kind === "funding" && !bankAccountName.trim())
                    }
                    onClick={() => mutation.mutate()}
                >
                    {mutation.isPending ? "Submitting…" : "Submit adjustment"}
                </button>
                {resultMsg ? <p className="apply-payment__ok">{resultMsg}</p> : null}
            </div>
            {errorMsg ? (
                <p className="danger apply-payment__error" role="alert">
                    {errorMsg}
                </p>
            ) : null}
        </section>
    );
};

export default ApplyAdjustmentPanel;
